/**
 * The bell: the person's own notification history, for everyone (plan notifications 4.5).
 * Varsler (/varsler) stays the manager's computed work list; the bell only links to it.
 * Radix Popover gives Escape, outside click and focus back on the trigger.
 */
import { type ApiNotification, useMarkRead, useNotifications, useUnreadCount } from '@/api/data';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { MONTHS } from '@/ui/format';
import { Icon } from '@/ui/icons';
import * as Popover from '@radix-ui/react-popover';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const OSLO = 'Europe/Oslo';
const day = (d: Date) => d.toLocaleDateString('en-CA', { timeZone: OSLO }); // YYYY-MM-DD
const clock = (d: Date) =>
  d.toLocaleTimeString('en-GB', { timeZone: OSLO, hour: '2-digit', minute: '2-digit' });

/** "i dag 14:05", "i går 09:30", "21. sep", or "21. sep 2025" for another year; read in Oslo. */
export function when(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const k = day(d);
  if (k === day(now)) return `${T('i dag')} ${clock(d)}`;
  if (k === day(new Date(now.getTime() - 86_400_000))) return `${T('i går')} ${clock(d)}`;
  const [y, m, dd] = k.split('-').map(Number);
  const short = `${dd}. ${(MONTHS[(m ?? 1) - 1] ?? '').slice(0, 3)}`;
  return y === Number(day(now).slice(0, 4)) ? short : `${short} ${y}`;
}

export function Bell() {
  const [open, setOpen] = useState(false);
  const can = useCan();
  const nav = useNavigate();
  const count = useUnreadCount().data ?? 0;
  const list = useNotifications(open);
  const mark = useMarkRead();
  const rows = list.data?.pages.flat() ?? [];

  const pick = (n: ApiNotification) => {
    if (!n.is_read) mark.mutate({ ids: [n.id] });
    if (n.web_url) {
      setOpen(false);
      nav(n.web_url);
    }
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="bell"
          aria-label={count ? T('Varsler, {n} uleste', { n: count }) : T('Varsler')}
        >
          <Icon name="bell" />
          {count > 0 && (
            <span className="n" aria-hidden="true">
              {count > 9 ? '9+' : count}
            </span>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="pop bellpop" align="end" sideOffset={6} aria-label={T('Varsler')}>
          <div className="bh">
            <h6>{T('Varsler')}</h6>
            {count > 0 && (
              <button type="button" className="lnk" onClick={() => mark.mutate({ all: true })}>
                {T('Merk alle som lest')}
              </button>
            )}
          </div>
          {list.isPending ? (
            <p className="bnone">{T('Laster…')}</p>
          ) : list.isError ? (
            <p className="bnone">{T('Kunne ikke laste varslene.')}</p>
          ) : !rows.length ? (
            <p className="bnone">{T('Ingen varsler ennå.')}</p>
          ) : (
            <ul>
              {rows.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    className={n.is_read ? '' : 'unread'}
                    data-testid="bell-row"
                    onClick={() => pick(n)}
                  >
                    {!n.is_read && <span className="sr">{T('Ulest')}: </span>}
                    <b>{n.title}</b>
                    <span>{n.message}</span>
                    <time dateTime={n.created_at}>{when(n.created_at)}</time>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {list.hasNextPage && (
            <button
              type="button"
              className="lnk more"
              disabled={list.isFetchingNextPage}
              onClick={() => list.fetchNextPage()}
            >
              {T('Vis flere')}
            </button>
          )}
          {can('timeliste.se') && (
            <Link className="bfoot" to="/varsler" onClick={() => setOpen(false)}>
              {T('Åpne Oppfølging')}
            </Link>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
